import React, { useEffect, useState, useCallback } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { SwipeableDrawer } from "@mui/material";

import { doc, getDoc, runTransaction, arrayUnion } from "firebase/firestore";

import { db } from "../../services/firebase.config";
import { useAuth } from "../../services/AuthContext";
import dataCache from "../../services/DataCache";
import { H1, P1, P3 } from "../theme/text/Headings";
import { ProPicSm, SecPicSm } from "../theme/ProfilePics";
import { DarkGrey, White, White05, White08 } from "../theme/colors";
import { ButtonOutlineSmall } from "../theme/Button";
import { DrawerLine } from "../theme/Drawer";

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0px 20px;
  text-decoration: none;
  color: inherit;
  justify-content: space-between;
`;

const ItemInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const NonLinkRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  color: inherit;
  flex: 1;
`;

const DrawerContent = styled.div`
  background: ${DarkGrey};
  border-radius: 30px 30px 0 0;
  overflow: hidden;
  max-height: 50vh;
  display: flex;
  flex-direction: column;
`;

const DrawerHeader = styled.div`
  padding: 20px 20px 20px 25px;
  text-align: left;
`;

const DrawerBody = styled.div`
  padding: 0 40px 40px 20px;
  overflow-y: auto;
  flex: 1;
`;

const DrawerLineWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const DrawerTitle = styled(H1)`
  color: ${White};
`;

const FriendLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  color: inherit;
  flex: 1;
`;

const InvitedText = styled(P3)`
  margin-right: 6px;
`;

export default function FriendsModal({ show, onClose, userId, title }) {
  const [loading, setLoading] = useState(false);
  const [friendProfiles, setFriendProfiles] = useState([]);
  const isOpen = !!show;
  const { currentUserState } = useAuth();
  const viewerId = currentUserState?.uid || null;
  const [viewerProfile, setViewerProfile] = useState(null);
  const [addingMap, setAddingMap] = useState({});

  const handleClose = useCallback(() => onClose(), [onClose]);
  const handleOpen = useCallback(() => {}, []);

  useEffect(() => {
    const fetchFriends = async () => {
      if (!isOpen || !userId) return;
      setLoading(true);
      try {
        // OPTIMIZED: Check cache first for profile data
        let data = dataCache.getUserProfile(userId);

        if (!data) {
          const profileRef = doc(db, "public-profiles", userId);
          const profileSnap = await getDoc(profileRef);
          if (!profileSnap.exists()) {
            setFriendProfiles([]);
            return;
          }
          data = profileSnap.data();
          // Cache for future use
          dataCache.setUserProfile(userId, data);
        }
        const friendIds = Array.isArray(data.friends) ? data.friends : [];
        if (friendIds.length === 0) {
          setFriendProfiles([]);
          return;
        }

        // OPTIMIZED: Fetch friend profiles in parallel with caching
        const profiles = await Promise.all(
          friendIds.map(async (fid) => {
            try {
              // Check cache first for friend profile
              let fData = dataCache.getUserProfile(fid);

              if (!fData) {
                const fRef = doc(db, "public-profiles", fid);
                const fSnap = await getDoc(fRef);
                if (!fSnap.exists()) return null;
                fData = fSnap.data();
                // Cache friend profile for future use
                dataCache.setUserProfile(fid, fData);
              }

              return {
                id: fid,
                displayName: fData.displayName || "No name",
                username: (fData.username || "").toLowerCase(),
                photoURL: fData.photoURL || "",
              };
            } catch (_) {
              return null;
            }
          })
        );

        const validProfiles = profiles.filter(Boolean);
        validProfiles.sort((a, b) =>
          (a.displayName || "").localeCompare(b.displayName || "")
        );
        setFriendProfiles(validProfiles);
      } finally {
        setLoading(false);
      }
    };
    fetchFriends();
  }, [isOpen, userId]);

  // Load viewer profile (to know existing friends and pending requests)
  useEffect(() => {
    const loadViewerProfile = async () => {
      if (!isOpen || !viewerId) return;
      try {
        // OPTIMIZED: Check cache first for viewer profile
        let viewerData = dataCache.getUserProfile(viewerId);

        if (!viewerData) {
          const vRef = doc(db, "public-profiles", viewerId);
          const vSnap = await getDoc(vRef);
          if (vSnap.exists()) {
            viewerData = vSnap.data();
            // Cache viewer profile for future use
            dataCache.setUserProfile(viewerId, viewerData);
          }
        }

        setViewerProfile(viewerData || null);
      } catch (_) {
        setViewerProfile(null);
      }
    };
    loadViewerProfile();
  }, [isOpen, viewerId]);

  const isPending = (friendId) => {
    const v = viewerProfile || {};
    const sent = v.friendRequests?.sent || [];
    const received = v.friendRequests?.received || [];
    return sent.includes(friendId) || received.includes(friendId);
  };

  const isAlreadyFriend = (friendId) => {
    const v = viewerProfile || {};
    const friends = Array.isArray(v.friends) ? v.friends : [];
    return friends.includes(friendId);
  };

  const sendFriendRequest = async (recipientId) => {
    if (!viewerId || recipientId === viewerId) return;
    setAddingMap((m) => ({ ...m, [recipientId]: true }));
    try {
      const userRef = doc(db, "public-profiles", viewerId);
      const recipientRef = doc(db, "public-profiles", recipientId);

      await runTransaction(db, async (transaction) => {
        const userDoc = await transaction.get(userRef);
        const friendDoc = await transaction.get(recipientRef);

        if (!userDoc.exists()) throw new Error("Your profile not found");
        if (!friendDoc.exists()) throw new Error("User profile not found");

        const userSent = userDoc.data().friendRequests?.sent || [];
        const friendReceived = friendDoc.data().friendRequests?.received || [];
        const userFriends = userDoc.data().friends || [];

        if (userFriends.includes(recipientId)) return; // already friends
        if (userSent.includes(recipientId)) return; // already sent
        if (friendReceived.includes(viewerId)) return; // already exists

        transaction.update(userRef, {
          "friendRequests.sent": arrayUnion(recipientId),
        });
        transaction.update(recipientRef, {
          "friendRequests.received": arrayUnion(viewerId),
        });
      });

      // Optimistically update viewerProfile state to reflect pending
      setViewerProfile((v) => {
        const sent = v?.friendRequests?.sent || [];
        const next = {
          ...(v || {}),
          friendRequests: {
            ...(v?.friendRequests || {}),
            sent: [...sent, recipientId],
          },
        };
        return next;
      });
    } catch (e) {
      alert(e?.message || "Failed to send request");
    } finally {
      setAddingMap((m) => ({ ...m, [recipientId]: false }));
    }
  };

  return (
    <SwipeableDrawer
      open={isOpen}
      onClose={handleClose}
      onOpen={handleOpen}
      anchor="bottom"
      disableSwipeToOpen={true}
      swipeAreaWidth={0}
      PaperProps={{
        sx: {
          backgroundColor: "transparent",
          boxShadow: "none",
          maxHeight: "70vh",
        },
      }}
      sx={{
        zIndex: 1300,
      }}
    >
      <DrawerContent>
        <DrawerLineWrapper>
          <DrawerLine />
        </DrawerLineWrapper>
        <DrawerHeader>
          <DrawerTitle>Friends</DrawerTitle>
        </DrawerHeader>
        <DrawerBody>
          {loading ? (
            <P3 color={White08}>Loading...</P3>
          ) : friendProfiles.length === 0 ? (
            <P3 color={White08}>No friends to show</P3>
          ) : (
            <List>
              {friendProfiles.map((fp) => (
                <Item key={fp.id}>
                  {isAlreadyFriend(fp.id) ? (
                    <FriendLink to={`/${fp.username}`} onClick={handleClose}>
                      {fp.photoURL ? (
                        <ProPicSm src={fp.photoURL} />
                      ) : (
                        <SecPicSm>
                          <H1>
                            {fp.displayName
                              ? fp.displayName[0].toUpperCase()
                              : "?"}
                          </H1>
                        </SecPicSm>
                      )}
                      <ItemInfo>
                        <P1 className="mb-5-real" color={White08}>
                          {fp.displayName}
                        </P1>
                        <P3 color={White05}>@{fp.username}</P3>
                      </ItemInfo>
                    </FriendLink>
                  ) : (
                    <NonLinkRow>
                      {fp.photoURL ? (
                        <ProPicSm src={fp.photoURL} />
                      ) : (
                        <SecPicSm>
                          <H1>
                            {fp.displayName
                              ? fp.displayName[0].toUpperCase()
                              : "?"}
                          </H1>
                        </SecPicSm>
                      )}
                      <ItemInfo>
                        <P1 className="mb-5-real" color={White08}>
                          {fp.displayName}
                        </P1>
                        <P3 color={White05}>@{fp.username}</P3>
                      </ItemInfo>
                    </NonLinkRow>
                  )}
                  {viewerId &&
                    fp.id !== viewerId &&
                    (isAlreadyFriend(fp.id) ? null : isPending(fp.id) ? (
                      <InvitedText color={White08}>Invited</InvitedText>
                    ) : (
                      <ButtonOutlineSmall
                        $background={White08}
                        onClick={() => sendFriendRequest(fp.id)}
                        disabled={addingMap[fp.id]}
                      >
                        {addingMap[fp.id] ? "Adding..." : "Add"}
                      </ButtonOutlineSmall>
                    ))}
                </Item>
              ))}
            </List>
          )}
        </DrawerBody>
      </DrawerContent>
    </SwipeableDrawer>
  );
}
